import { useEffect } from 'react';

// The keyboard moves between the fields of a form: Enter goes to the next one, the arrow keys
// go either way.
//
// Asked for app-wide, so it is ONE pair of listeners mounted in App.js rather than props
// threaded through thirty forms - a form written next year gets the behaviour without knowing
// about it.
//
// It only acts inside a form surface (a Drawer paper, a Dialog paper or a real <form>), which
// is where every form in this app lives. Outside one - the Digital Marketing chat and its inline
// title editors, a section's search box - the keys keep whatever meaning they already had. A
// screen that is not a Drawer or a Dialog but should still be walked with the arrow keys (the
// login card) opts in with data-arrow-surface.
//
// ── ENTER ────────────────────────────────────────────────────────────────────────────────────
// Enter does what Tab does, but only on a field it is safe to take Enter from. It refuses to act
// on anything that owns Enter itself:
//   · a textarea or multiline field, where Enter is a new line
//   · a rich text editor (TipTap renders contenteditable), same reason
//   · a button or link, where Enter activates it
//   · an Autocomplete or Select with its list open, where Enter picks the highlighted option
//   · a field whose own onKeyDown already called preventDefault - that handler owns the key
//   · anything inside [data-enter-advance="off"], the escape hatch for a future exception
// Checkboxes, radios and switches are neither a source nor a destination for Enter: Enter walks
// the fields you TYPE in. The last field hands focus to the form's primary button WITHOUT
// pressing it, so a second Enter saves. Nothing is ever submitted by surprise.
//
// ── ARROWS ───────────────────────────────────────────────────────────────────────────────────
// ArrowDown / ArrowUp go to the next / previous field - Tab and Shift+Tab, on the keys a person
// reaches for. That includes dropdowns and checkboxes, which Enter skips: a field you cannot get
// to with the arrow keys is a field you have to leave the keyboard for. The price is paid in one
// place, on purpose: on a dropdown the arrows MOVE instead of changing or opening it (otherwise a
// person walking down a form would change a status without meaning to). Space, a click, or typing
// the first letters still choose, and Alt+Down is left to the browser to open a native one.
// ArrowLeft / ArrowRight move the CARET as they always did, and only when the caret has nowhere
// left to go - at the very start or end of a single-line field - do they hop to the previous /
// next field, in the direction the text reads (swapped inside a right-to-left field).
// What keeps its own arrow keys:
//   · an Autocomplete or Select with its list open - the arrows pick from the list
//   · radio buttons, sliders and the native date/time inputs - their arrows ARE their input
//     (Tab leaves them), and a rich text editor
//   · a textarea except at its very first or last position - elsewhere the arrows move the caret
//   · a field whose own onKeyDown already called preventDefault, and [data-arrow-nav="off"] (or
//     [data-enter-advance="off"]) around anything that has to opt out
// Shift, Ctrl, Alt and Meta are never touched, so selecting text and every browser shortcut work.

const FORM_SURFACE = 'form, .MuiDialog-paper, .MuiDrawer-paper';
const ARROW_SURFACE = `${FORM_SURFACE}, [data-arrow-surface]`;

// What Enter can land ON. A textarea is a destination (Enter inside it then makes a new line)
// but never a source. MUI renders a Select as a div, so it is matched by class, not by tag.
const DESTINATIONS = [
  'input:not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="reset"])'
  + ':not([type="checkbox"]):not([type="radio"]):not([type="file"])',
  'textarea',
  'select',
  '.MuiSelect-select',
].join(',');

// What an ARROW can land on: everything Enter lands on plus checkboxes and switches. Radios are
// left out on purpose - their own arrow keys walk the group, so a radio would trap the person.
const ARROW_DESTINATIONS = [
  'input:not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="reset"])'
  + ':not([type="radio"]):not([type="file"]):not([type="image"]):not([type="range"]):not([type="color"])',
  'textarea',
  'select',
  '.MuiSelect-select',
].join(',');

// Inputs whose arrow keys are the input itself.
const OWN_ARROW_TYPES = ['radio', 'range', 'date', 'time', 'datetime-local', 'month', 'week', 'color', 'file'];
const NOT_A_FIELD_TYPES = ['submit', 'button', 'reset', 'hidden', 'image'];

// A field the user cannot see or reach is not a destination. MUI keeps a hidden native input
// beside every Select, which is exactly what the tabIndex / size checks drop.
const reachable = (el) => !el.disabled
  && el.getAttribute('aria-hidden') !== 'true'
  && el.tabIndex !== -1
  && Boolean(el.offsetWidth || el.offsetHeight || el.getClientRects().length);

// Save / Create / Send: the contained button, which is how every form in this app marks its
// primary action. Falling back to a real submit button covers a plain <form>.
function primaryButton(scope) {
  const submit = scope.querySelector('button[type="submit"]:not(:disabled)');
  if (submit && reachable(submit)) return submit;
  const contained = [...scope.querySelectorAll('button.MuiButton-contained:not(:disabled)')].filter(reachable);
  return contained.length ? contained[contained.length - 1] : null;
}

// Land ready to overtype rather than with the caret wherever the click happened to be - what
// Tab does in a browser.
function landOn(target) {
  target.focus();
  if (typeof target.select === 'function' && target.tagName === 'INPUT' && target.type !== 'checkbox') {
    try { target.select(); } catch (_) { /* a date/number input may refuse; focus is enough */ }
  }
}

function advance(scope, current) {
  const fields = [...scope.querySelectorAll(DESTINATIONS)].filter(reachable);
  const here = fields.indexOf(current);
  const next = here === -1 ? null : fields[here + 1];
  const target = next || primaryButton(scope);
  if (!target) return false;
  landOn(target);
  return true;
}

// Enter we are allowed to look at at all, in either phase.
function liveEnter(event) {
  if (event.key !== 'Enter') return false;
  // Mid-composition (an IME is still assembling a character) Enter commits that character.
  if (event.isComposing || event.keyCode === 229) return false;
  // Shift+Enter is a new line, and the modifier combinations belong to the browser.
  if (event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) return false;
  const el = event.target;
  if (!el || el.nodeType !== 1) return false;
  return true;
}

// The form this field belongs to, or null when it belongs to none of them.
function formScope(el) {
  const scope = el.closest(FORM_SURFACE);
  if (!scope) return null;
  if (el.closest('[data-enter-advance="off"]')) return null;
  return scope;
}

// ── arrows ───────────────────────────────────────────────────────────────────────────────────

const ARROW_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];

function liveArrow(event) {
  if (!ARROW_KEYS.includes(event.key)) return false;
  if (event.isComposing || event.keyCode === 229) return false;
  if (event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) return false;
  const el = event.target;
  return Boolean(el && el.nodeType === 1);
}

function arrowScope(el) {
  const scope = el.closest(ARROW_SURFACE);
  if (!scope) return null;
  if (el.closest('[data-arrow-nav="off"], [data-enter-advance="off"]')) return null;
  return scope;
}

// A caret that cannot move any further hands the arrow key to the form: at the start it goes
// back, at the end it goes on - "forward" being the way the text reads, so the keys swap inside a
// right-to-left field. A selection, or a caret in the middle of the text, is left alone: the
// arrow collapses or moves it, as it always did. Inputs that cannot report a caret (number,
// email) answer null and are walked with Up and Down only.
function caretStep(el, key) {
  if (typeof el.selectionStart !== 'number' || el.selectionStart !== el.selectionEnd) return 0;
  const rtl = window.getComputedStyle(el).direction === 'rtl';
  const forward = key === (rtl ? 'ArrowLeft' : 'ArrowRight');
  const backward = key === (rtl ? 'ArrowRight' : 'ArrowLeft');
  if (forward && el.selectionEnd === el.value.length) return 1;
  if (backward && el.selectionStart === 0) return -1;
  return 0;
}

/** +1 (next field), -1 (previous field), or 0 when the arrow belongs to the element itself. */
function arrowStep(el, key) {
  if (el.isContentEditable) return 0;
  const vertical = key === 'ArrowUp' || key === 'ArrowDown';
  const vstep = key === 'ArrowDown' ? 1 : -1;
  const tag = el.tagName;

  // A closed dropdown: the arrows walk past it (they would otherwise change or open it).
  if (tag === 'SELECT' || el.classList.contains('MuiSelect-select')) {
    if (el.getAttribute('aria-expanded') === 'true') return 0;
    return vertical ? vstep : 0;
  }
  // A real button (the form's own Save and friends) - so a person who arrowed to the end of the
  // form, or pressed Enter on the last field, can arrow back. Tabs, menu items and icon buttons
  // have arrow behaviour or no business here.
  if (tag === 'BUTTON') {
    if (!el.classList.contains('MuiButton-root') || el.getAttribute('role') === 'tab') return 0;
    return vertical ? vstep : 0;
  }
  if (tag === 'INPUT') {
    const type = String(el.type || '').toLowerCase();
    if (OWN_ARROW_TYPES.includes(type) || NOT_A_FIELD_TYPES.includes(type)) return 0;
    // An Autocomplete whose list is open: the arrows pick from it.
    if (el.getAttribute('aria-expanded') === 'true') return 0;
    // An Autocomplete's Left and Right walk its chips, so only Up and Down leave it.
    if (el.getAttribute('role') === 'combobox') return vertical ? vstep : 0;
    return vertical ? vstep : caretStep(el, key);
  }
  if (tag === 'TEXTAREA') {
    if (vertical) {
      // Only from the very first / last position: everywhere else Up and Down move the caret
      // between lines, which is what a textarea is for.
      const start = el.selectionStart === 0 && el.selectionEnd === 0;
      const end = el.selectionStart === el.value.length && el.selectionEnd === el.value.length;
      if (key === 'ArrowUp' && start) return -1;
      if (key === 'ArrowDown' && end) return 1;
      return 0;
    }
    return caretStep(el, key);
  }
  return 0;
}

// The field a step away. A focused element that is not itself a field (the Save button) is
// placed by where it sits: the nearest field before it, or after it.
function neighbour(fields, from, step) {
  const at = fields.indexOf(from);
  if (at !== -1) return fields[at + step] || null;
  if (step > 0) {
    return fields.find((f) => from.compareDocumentPosition(f) & Node.DOCUMENT_POSITION_FOLLOWING) || null;
  }
  const before = fields.filter((f) => from.compareDocumentPosition(f) & Node.DOCUMENT_POSITION_PRECEDING);
  return before.length ? before[before.length - 1] : null;
}

function moveFocus(scope, from, step) {
  const fields = [...scope.querySelectorAll(ARROW_DESTINATIONS)].filter((f) => reachable(f) && !f.readOnly);
  const target = neighbour(fields, from, step);
  if (!target) return false;
  landOn(target);
  return true;
}

// A MUI Select and an Autocomplete act on the arrow keys in their OWN handler, before it ever
// reaches the document (they open their list), so those two have to be caught on the way down.
// Everything else is handled on the way back up, after the field's own onKeyDown has had its say.
const needsCapture = (el) => el.classList.contains('MuiSelect-select') || el.getAttribute('role') === 'combobox';

function handleArrow(event, capturing) {
  const el = event.target;
  if (needsCapture(el) !== capturing) return;
  if (!capturing && event.defaultPrevented) return;
  const scope = arrowScope(el);
  if (!scope) return;
  const step = arrowStep(el, event.key);
  if (!step) return;
  moveFocus(scope, el, step);
  // Swallowed even at the first or last field: the arrow was meant for navigation, and letting it
  // through there would change a native select's value or step a number input.
  event.preventDefault();
  if (capturing) event.stopPropagation();
}

export default function useEnterAdvancesFields() {
  useEffect(() => {
    // A CLOSED MUI Select has to be caught before React hands the key to MUI, which would open
    // the menu instead. That matters more than the affordance it costs: picking an option
    // returns focus to the same select, so an Enter that re-opens the menu is a field the user
    // can never get past once Tab is no longer how they move. Space still opens it.
    const onCapture = (event) => {
      if (liveArrow(event)) { handleArrow(event, true); return; }
      if (!liveEnter(event)) return;
      const el = event.target;
      if (!el.classList || !el.classList.contains('MuiSelect-select')) return;
      if (el.getAttribute('aria-expanded') === 'true') return;
      if (el.getAttribute('aria-disabled') === 'true') return;
      const scope = formScope(el);
      if (!scope) return;
      if (!advance(scope, el)) return;
      event.preventDefault();
      // MUI reads the key, not defaultPrevented, so the event has to stop here.
      event.stopPropagation();
    };

    const onKeyDown = (event) => {
      if (liveArrow(event)) { handleArrow(event, false); return; }
      if (!liveEnter(event)) return;
      // A field's own handler already dealt with it - adding a tag, sending a message, picking
      // an Autocomplete option. Never act twice on one key.
      if (event.defaultPrevented) return;

      const el = event.target;
      const scope = formScope(el);
      if (!scope) return;
      if (el.isContentEditable) return;

      const tag = el.tagName;
      const isMuiSelect = el.classList.contains('MuiSelect-select');
      if (!isMuiSelect && tag !== 'SELECT') {
        if (tag !== 'INPUT') return;    // textarea, button, link, a rich text editor's div
        if (el.getAttribute('role') === 'button') return;
        const type = String(el.type || '').toLowerCase();
        if (['submit', 'button', 'reset', 'checkbox', 'radio', 'file'].includes(type)) return;
      }
      // An open list means Enter is choosing from it.
      if (el.getAttribute('aria-expanded') === 'true') return;

      if (advance(scope, el)) event.preventDefault();
    };

    document.addEventListener('keydown', onCapture, true);
    // Bubble phase on purpose: a field's own onKeyDown runs first and can claim the key.
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onCapture, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);
}
